// Personal cabinet: manage own API tokens (mespat_…) for connecting MCP
// agents (Claude Code etc.) to /api/v1/mcp. Mirrors the "show raw secret
// once" modal pattern from PrintAgentsList/PrintAgentDetail — after the
// create response, only sha256(token) survives server-side.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { useAuth } from "@/lib/AuthContext";
import { formatDateTime } from "@/lib/format";
import {
  apiTokensApi,
  type ApiTokenCreateResponse,
  type ApiTokenRead,
  type SetupLinkResponse,
} from "@/lib/api";

const MCP_URL = `${window.location.origin}/api/v1/mcp`;
const EXPIRY_OPTIONS = [7, 30, 90, 365] as const;

function mcpAddCommand(placeholder: string): string {
  return `claude mcp add --transport http mes ${MCP_URL} --header "Authorization: Bearer <${placeholder}>"`;
}

type TokenStatus = "active" | "revoked" | "expired";

function tokenStatus(t: ApiTokenRead): TokenStatus {
  if (t.revoked_at) return "revoked";
  if (t.expires_at && new Date(t.expires_at).getTime() < Date.now()) return "expired";
  return "active";
}

export function ApiTokensCard() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canIssue = hasPermission("api_token.issue");
  const qc = useQueryClient();
  const [created, setCreated] = useState<ApiTokenCreateResponse | null>(null);
  const [setupLink, setSetupLink] = useState<SetupLinkResponse | null>(null);

  const q = useQuery({
    queryKey: ["auth", "api-tokens"],
    queryFn: () => apiTokensApi.list(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["auth", "api-tokens"] });

  return (
    <section className="card p-5 space-y-4 md:col-span-2">
      <header>
        <h3 className="text-base">{t("apiTokens.cardTitle")}</h3>
        <p className="text-text-muted text-xs mt-1">{t("apiTokens.help1")}</p>
        <p className="text-text-muted text-xs mt-1">
          {t("apiTokens.help2")} <code className="font-mono">{MCP_URL}</code>
        </p>
        <code className="block font-mono text-xs mt-2 px-3 py-2 rounded-md bg-surface border border-border break-all">
          {mcpAddCommand(t("apiTokens.tokenPlaceholder"))}
        </code>
      </header>

      {q.isLoading && <p className="text-text-muted text-sm">{t("common.loading")}</p>}
      {q.isError && <ErrorBanner>{q.error instanceof Error ? q.error.message : t("common.error")}</ErrorBanner>}

      {q.data && q.data.items.length === 0 && (
        <p className="text-text-muted text-sm">{t("apiTokens.empty")}</p>
      )}

      {q.data && q.data.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="py-2 pr-3">{t("apiTokens.colName")}</th>
                <th className="py-2 pr-3">{t("apiTokens.colCreated")}</th>
                <th className="py-2 pr-3">{t("apiTokens.colLastUsed")}</th>
                <th className="py-2 pr-3">{t("apiTokens.colExpires")}</th>
                <th className="py-2 pr-3">{t("apiTokens.colStatus")}</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((tok) => (
                <TokenRow key={tok.id} tok={tok} onRevoked={invalidate} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canIssue ? (
        <IssueForm
          onCreated={(resp) => { invalidate(); setCreated(resp); }}
          onSetupLink={(resp) => setSetupLink(resp)}
        />
      ) : (
        <p className="text-text-muted text-xs border-t border-border pt-4">
          {t("apiTokens.noIssuePermission")}
        </p>
      )}

      {created && <TokenModal data={created} onClose={() => setCreated(null)} />}
      {setupLink && (
        <SetupLinkModal
          data={setupLink}
          onClose={() => {
            setSetupLink(null);
            // The token appears in the list only once the link is
            // consumed — refetch so a quick agent shows up right away.
            void invalidate();
          }}
        />
      )}
    </section>
  );
}

function TokenRow({
  tok,
  onRevoked,
}: {
  tok: ApiTokenRead;
  onRevoked: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const status = tokenStatus(tok);

  const revoke = useMutation({
    mutationFn: () => apiTokensApi.revoke(tok.id),
    onSuccess: () => {
      setConfirming(false);
      onRevoked();
    },
  });

  return (
    <tr className="border-t border-border">
      <td className="py-2 pr-3">{tok.name}</td>
      <td className="py-2 pr-3 text-text-muted text-xs whitespace-nowrap">
        {formatDateTime(tok.created_at)}
      </td>
      <td className="py-2 pr-3 text-text-muted text-xs whitespace-nowrap">
        {tok.last_used_at ? formatDateTime(tok.last_used_at) : "—"}
      </td>
      <td className="py-2 pr-3 text-text-muted text-xs whitespace-nowrap">
        {tok.expires_at ? formatDateTime(tok.expires_at) : t("apiTokens.noExpiry")}
      </td>
      <td className="py-2 pr-3">
        <span
          className={
            "pill text-xs " +
            (status === "active" ? "pill-ok" : status === "expired" ? "pill-warn" : "pill-danger")
          }
        >
          {t(`apiTokens.status.${status}`)}
        </span>
      </td>
      <td className="py-2 pr-3 text-right whitespace-nowrap">
        {status === "active" && (
          confirming ? (
            <span className="inline-flex items-center gap-1">
              <span className="text-text-muted text-xs">{t("apiTokens.confirmRevoke")}</span>
              <button
                type="button"
                className="btn btn-danger text-xs"
                onClick={() => revoke.mutate()}
                disabled={revoke.isPending}
              >
                {revoke.isPending ? t("common.deleting") : t("common.yes")}
              </button>
              <button type="button" className="btn text-xs" onClick={() => setConfirming(false)}>
                {t("common.no")}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn-danger text-xs"
              onClick={() => setConfirming(true)}
            >
              {t("apiTokens.revoke")}
            </button>
          )
        )}
      </td>
    </tr>
  );
}

function IssueForm({
  onCreated,
  onSetupLink,
}: {
  onCreated: (resp: ApiTokenCreateResponse) => void;
  onSetupLink: (resp: SetupLinkResponse) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);

  const payload = () => ({
    name: name.trim(),
    ...(expiry ? { expires_in_days: Number(expiry) } : {}),
  });

  const resetFields = () => {
    setName("");
    setExpiry("");
  };

  const mut = useMutation({
    mutationFn: () => apiTokensApi.create(payload()),
    onSuccess: (resp) => {
      resetFields();
      onCreated(resp);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  // Same fields, different result: instead of issuing the token right
  // here, mint a one-time onboarding URL the operator hands to their
  // agent; the token is created when the link is opened.
  const linkMut = useMutation({
    mutationFn: () => apiTokensApi.setupLink(payload()),
    onSuccess: (resp) => {
      resetFields();
      onSetupLink(resp);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  const validate = (): boolean => {
    setErr(null);
    if (!name.trim()) {
      setErr(t("apiTokens.errNameRequired"));
      return false;
    }
    return true;
  };

  const busy = mut.isPending || linkMut.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (validate()) mut.mutate();
      }}
      className="border-t border-border pt-4 grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto_auto] gap-2 items-end"
    >
      <Field label={t("apiTokens.nameLabel")}>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
          placeholder={t("apiTokens.namePlaceholder")}
        />
      </Field>
      <Field label={t("apiTokens.expiryLabel")}>
        <select
          value={expiry}
          onChange={(e) => setExpiry(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
        >
          <option value="">{t("apiTokens.noExpiry")}</option>
          {EXPIRY_OPTIONS.map((d) => (
            <option key={d} value={d}>
              {t("apiTokens.expiryDays", { count: d })}
            </option>
          ))}
        </select>
      </Field>
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {mut.isPending ? t("common.creating") : t("apiTokens.issue")}
      </button>
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={() => {
          if (validate()) linkMut.mutate();
        }}
      >
        {linkMut.isPending ? t("common.creating") : t("apiTokens.setupLink")}
      </button>
      {err && (
        <div className="sm:col-span-4">
          <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>
        </div>
      )}
    </form>
  );
}

function TokenModal({
  data,
  onClose,
}: {
  data: ApiTokenCreateResponse;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked — the operator can still copy manually
      // from the visible <code> block below.
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card max-w-2xl w-full p-6 space-y-4">
        <header>
          <h2 className="text-lg">{t("apiTokens.tokenCreated")}</h2>
          <p className="text-text-muted text-sm mt-1">
            {t("apiTokens.tokenWarning1")} <strong>{t("apiTokens.tokenWarningBold")}</strong>{t("apiTokens.tokenWarning2")}
          </p>
        </header>

        <section className="space-y-2">
          <Field label={t("apiTokens.nameLabel")}>
            <p className="font-mono text-sm">{data.api_token.name}</p>
          </Field>
          <Field label={t("apiTokens.bearerToken")}>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 px-3 py-2 rounded-md bg-surface border border-border text-xs font-mono break-all">
                {data.token}
              </code>
              <button type="button" className="btn" onClick={copy}>
                {copied ? t("common.copied") : t("common.copy")}
              </button>
            </div>
          </Field>
          <p className="text-text-muted text-xs">
            {t("apiTokens.mcpHint")} <code className="font-mono">{MCP_URL}</code>
          </p>
        </section>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn btn-primary" onClick={onClose}>
            {t("apiTokens.savedClose")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SetupLinkModal({
  data,
  onClose,
}: {
  data: SetupLinkResponse;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const agentPrompt = t("apiTokens.agentPrompt", { url: data.url });
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  const copy = async (text: string, mark: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      mark(true);
      setTimeout(() => mark(false), 2000);
    } catch {
      // Clipboard API blocked — the operator can still copy manually
      // from the visible <code> blocks below.
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card max-w-2xl w-full p-6 space-y-4">
        <header>
          <h2 className="text-lg">{t("apiTokens.setupLinkTitle")}</h2>
          <p className="text-text-muted text-sm mt-1">{t("apiTokens.setupLinkHelp")}</p>
        </header>

        <section className="space-y-3">
          <Field label={t("apiTokens.setupLinkUrlLabel")}>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 px-3 py-2 rounded-md bg-surface border border-border text-xs font-mono break-all">
                {data.url}
              </code>
              <button
                type="button"
                className="btn"
                onClick={() => copy(data.url, setCopiedUrl)}
              >
                {copiedUrl ? t("common.copied") : t("common.copy")}
              </button>
            </div>
          </Field>
          <Field label={t("apiTokens.agentPromptLabel")}>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 px-3 py-2 rounded-md bg-surface border border-border text-xs font-mono break-all">
                {agentPrompt}
              </code>
              <button
                type="button"
                className="btn"
                onClick={() => copy(agentPrompt, setCopiedPrompt)}
              >
                {copiedPrompt ? t("common.copied") : t("common.copy")}
              </button>
            </div>
          </Field>
        </section>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn btn-primary" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
